import { ErrorResponse } from 'react-router-dom';
import {
  IMotorInsuranceDataSource,
  IMotorInsuranceInitialValues,
  IResponse,
} from '../types';
import { IUseHttpServiceProps } from '../services/type';
import { RequestMethods } from '../services/enum';
import { prepareFormData } from '../utils/functions';
import { IExpandedRow } from '../components/DataTable/type';

export default class MotorInsuranceService {
  private readonly baseUrl = 'motor-insurance/motor-insurance/';

  constructor(
    private readonly executeService: <T>(
      args: IUseHttpServiceProps,
    ) => Promise<T | ErrorResponse | null>,
    private readonly parameters: string,
  ) {}

  async fetchData(): Promise<
    IResponse<IMotorInsuranceDataSource> | ErrorResponse | null
  > {
    return await this.executeService<IResponse<IMotorInsuranceDataSource>>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${this.parameters}`,
    });
  }

  async fetchItemById(
    id: number,
  ): Promise<IMotorInsuranceDataSource | ErrorResponse | null> {
    return await this.executeService<IMotorInsuranceDataSource>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${id}/`,
    });
  }

  async fetchExpandedRows(
    id: number,
  ): Promise<IExpandedRow[] | ErrorResponse | null> {
    return await this.executeService<IExpandedRow[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${id}/get-latest-processes/`,
    });
  }

  async deleteItemById(id: number): Promise<unknown | ErrorResponse | null> {
    return await this.executeService<unknown>({
      method: RequestMethods.DELETE,
      url: `${this.baseUrl}${id}/`,
    });
  }

  async exportData(): Promise<{ download_url: string } | ErrorResponse | null> {
    return await this.executeService<{ download_url: string }>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}export-excel/${this.parameters}`,
      contentType: 'multipart/form-data',
    });
  }

  async submitForm(
    values: IMotorInsuranceInitialValues,
    selectedItem?: IMotorInsuranceDataSource,
  ): Promise<IMotorInsuranceDataSource | ErrorResponse | null> {
    const formData = prepareFormData<IMotorInsuranceInitialValues>(values);

    const method = selectedItem ? RequestMethods.PATCH : RequestMethods.POST;
    const url = selectedItem
      ? `${this.baseUrl}${selectedItem.id}/`
      : this.baseUrl;

    return await this.executeService<IMotorInsuranceDataSource>({
      method,
      url,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async updateStatus(
    id: number,
    status: string,
    email_users: number[],
  ): Promise<IMotorInsuranceDataSource | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('status', status);
    formData.append('email_users', email_users.join(','));

    return await this.executeService<IMotorInsuranceDataSource>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/update-status/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async cancelPolicy(
    id: number,
    file: File,
    description: string,
  ): Promise<unknown | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('id', id.toString());
    formData.append('file', file);
    formData.append('description', description);

    return await this.executeService<unknown>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/cancel-motor-insurance/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async addendumPolicy(
    id: number,
    file: File | null,
    license_plate: string,
    description: string,
  ): Promise<unknown | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('id', id.toString());
    if (file) formData.append('file', file);
    formData.append('license_plate', license_plate);
    formData.append('description', description);

    return await this.executeService<unknown>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/endorsement-request/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async uploadFiles(
    id: number,
    files: FileList,
  ): Promise<IMotorInsuranceDataSource | ErrorResponse | null> {
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('upload_files', file);
    });

    return await this.executeService<IMotorInsuranceDataSource>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/upload-files/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async deleteFile(
    id: number,
    fileId: number,
  ): Promise<IMotorInsuranceDataSource | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('file_id', fileId.toString());

    return await this.executeService<IMotorInsuranceDataSource>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/delete-file/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async requestAddendum(
    id: number,
    values: IMotorInsuranceInitialValues,
  ): Promise<unknown | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('id', values.id?.toString() || '0');
    formData.append('file', values.upload_files?.[0] || '');
    formData.append('license_plate', values.license_plate);
    formData.append('description', values.description);

    return await this.executeService<unknown>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/endorsement-request/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }
}
